// data/countryDialCodes.js
// The twelve dial codes offered on the training form, each with its own flag.
//
// The flags are drawn here rather than pulled in as emoji, because the regional
// indicator pairs do not render on Windows and fall back to two bare letters,
// and rather than fetched as images, because nothing on this page should depend
// on an external host.
//
// Each flag is a list of shapes on a 0 0 20 14 canvas, painted in order. A shape
// carrying r is a circle, anything else is a rect. They are deliberately crude:
// at 20px wide no detail survives, so the job is only to be recognisable at a
// glance. Where a flag cannot be approximated with rects it falls back to the
// field colour plus one distinguishing band.
//
// This file is data only, so it holds no JSX and imports nothing.

export const FLAG_VIEWBOX = "0 0 20 14";

export const COUNTRY_CODES = [
  {
    id: "IN",
    name: "India",
    dial: "+91",
    flag: [
      { x: 0, y: 0, w: 20, h: 4.67, fill: "#FF9933" },
      { x: 0, y: 4.67, w: 20, h: 4.66, fill: "#FFFFFF" },
      { x: 0, y: 9.33, w: 20, h: 4.67, fill: "#138808" },
      { cx: 10, cy: 7, r: 1.5, fill: "#000080" },
    ],
  },
  {
    id: "US",
    name: "United States",
    dial: "+1",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#FFFFFF" },
      { x: 0, y: 0, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 2.16, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 4.32, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 6.48, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 8.64, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 10.8, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 12.96, w: 20, h: 1.08, fill: "#B22234" },
      { x: 0, y: 0, w: 8.5, h: 7.54, fill: "#3C3B6E" },
    ],
  },
  {
    id: "GB",
    name: "United Kingdom",
    dial: "+44",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#012169" },
      { x: -2.5, y: 5.3, w: 25, h: 3.4, fill: "#FFFFFF", rot: 35 },
      { x: -2.5, y: 5.3, w: 25, h: 3.4, fill: "#FFFFFF", rot: -35 },
      { x: -2.5, y: 6.3, w: 25, h: 1.4, fill: "#C8102E", rot: 35 },
      { x: -2.5, y: 6.3, w: 25, h: 1.4, fill: "#C8102E", rot: -35 },
      { x: 7.5, y: 0, w: 5, h: 14, fill: "#FFFFFF" },
      { x: 0, y: 4.5, w: 20, h: 5, fill: "#FFFFFF" },
      { x: 8.5, y: 0, w: 3, h: 14, fill: "#C8102E" },
      { x: 0, y: 5.5, w: 20, h: 3, fill: "#C8102E" },
    ],
  },
  {
    id: "AE",
    name: "United Arab Emirates",
    dial: "+971",
    flag: [
      { x: 0, y: 0, w: 20, h: 4.67, fill: "#00732F" },
      { x: 0, y: 4.67, w: 20, h: 4.66, fill: "#FFFFFF" },
      { x: 0, y: 9.33, w: 20, h: 4.67, fill: "#000000" },
      { x: 0, y: 0, w: 5, h: 14, fill: "#CE1126" },
    ],
  },
  {
    id: "SG",
    name: "Singapore",
    dial: "+65",
    flag: [
      { x: 0, y: 0, w: 20, h: 7, fill: "#EF3340" },
      { x: 0, y: 7, w: 20, h: 7, fill: "#FFFFFF" },
      { cx: 4.5, cy: 3.5, r: 2, fill: "#FFFFFF" },
    ],
  },
  {
    id: "AU",
    name: "Australia",
    dial: "+61",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#012169" },
      { x: 0, y: 0, w: 9, h: 7, fill: "#FFFFFF" },
      { x: 3.75, y: 0, w: 1.5, h: 7, fill: "#E4002B" },
      { x: 0, y: 2.75, w: 9, h: 1.5, fill: "#E4002B" },
      { cx: 14, cy: 10, r: 1.6, fill: "#FFFFFF" },
    ],
  },
  {
    id: "CA",
    name: "Canada",
    dial: "+1",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#FFFFFF" },
      { x: 0, y: 0, w: 5, h: 14, fill: "#D80621" },
      { x: 15, y: 0, w: 5, h: 14, fill: "#D80621" },
      { x: 9, y: 4, w: 2, h: 6, fill: "#D80621" },
    ],
  },
  {
    id: "DE",
    name: "Germany",
    dial: "+49",
    flag: [
      { x: 0, y: 0, w: 20, h: 4.67, fill: "#000000" },
      { x: 0, y: 4.67, w: 20, h: 4.66, fill: "#DD0000" },
      { x: 0, y: 9.33, w: 20, h: 4.67, fill: "#FFCE00" },
    ],
  },
  {
    id: "SA",
    name: "Saudi Arabia",
    dial: "+966",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#006C35" },
      { x: 3, y: 8.6, w: 14, h: 1.2, fill: "#FFFFFF" },
    ],
  },
  {
    id: "MY",
    name: "Malaysia",
    dial: "+60",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#FFFFFF" },
      { x: 0, y: 0, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 2, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 4, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 6, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 8, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 10, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 12, w: 20, h: 1, fill: "#CC0001" },
      { x: 0, y: 0, w: 10, h: 8, fill: "#010066" },
      { cx: 4.5, cy: 4, r: 1.8, fill: "#FFCC00" },
    ],
  },
  {
    id: "QA",
    name: "Qatar",
    dial: "+974",
    flag: [
      { x: 0, y: 0, w: 20, h: 14, fill: "#8A1538" },
      { x: 0, y: 0, w: 6, h: 14, fill: "#FFFFFF" },
    ],
  },
  {
    id: "OM",
    name: "Oman",
    dial: "+968",
    flag: [
      { x: 0, y: 0, w: 20, h: 4.67, fill: "#FFFFFF" },
      { x: 0, y: 4.67, w: 20, h: 4.66, fill: "#DB161B" },
      { x: 0, y: 9.33, w: 20, h: 4.67, fill: "#008000" },
      { x: 0, y: 0, w: 6, h: 14, fill: "#DB161B" },
    ],
  },
];

// Keyed by country rather than by dial code, because +1 covers both the
// United States and Canada.
export const dialFor = (id) =>
  (COUNTRY_CODES.find((c) => c.id === id) || COUNTRY_CODES[0]).dial;

export const countryFor = (id) =>
  COUNTRY_CODES.find((c) => c.id === id) || COUNTRY_CODES[0];
